import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { US_STATES, loadUsPlaces, stateName, toStateCode } from '../util/usPlaces';

// City / State / ZIP for checkout. In the US: State is a searchable dropdown
// (stores the 2-letter code ShipStation and the gateways expect), City
// suggests the chosen state's cities as you type (any city can still be
// typed), and a 5-digit ZIP fills in city + state. Other countries keep the
// plain text fields.

const Combo = ({ value, display, placeholder, options, onPick, onType, onFocus, inputStyle, dark, ariaLabel, inputMode, free }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(null); // null = show `display`, string = user is searching
  const [hi, setHi] = useState(0);
  const listRef = useRef(null);
  const query = (q ?? '').trim().toLowerCase();
  const shown = useMemo(() => {
    if (!query) return options.slice(0, 60);
    const starts = []; const inside = [];
    for (const o of options) {
      const l = o.label.toLowerCase();
      if (l.startsWith(query) || o.value.toLowerCase() === query) starts.push(o);
      else if (l.includes(query)) inside.push(o);
      if (starts.length >= 40) break;
    }
    return [...starts, ...inside].slice(0, 40);
  }, [options, query]);
  useEffect(() => { setHi(0); }, [query]);
  useEffect(() => {
    const el = listRef.current?.children?.[hi];
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  const pick = (o) => { onPick(o); setQ(null); setOpen(false); };
  const close = () => {
    // Free-text fields (City) keep what was typed; strict ones (State) accept an exact match only.
    if (q != null && !free) {
      const exact = options.find((o) => o.value.toLowerCase() === query || o.label.toLowerCase() === query);
      if (exact) onPick(exact);
    }
    setQ(null); setOpen(false);
  };
  const bg = dark ? '#1a1d22' : '#fff';
  return <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
    <input style={{ ...inputStyle, paddingRight: 26 }} aria-label={ariaLabel} placeholder={placeholder} autoComplete="off" inputMode={inputMode}
      role="combobox" aria-expanded={open} aria-autocomplete="list"
      value={q ?? display}
      onFocus={() => { onFocus?.(); setOpen(true); }}
      onBlur={close}
      onChange={(e) => { setQ(e.target.value); setOpen(true); onType?.(e.target.value); }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, shown.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        else if (e.key === 'Enter' && open && shown[hi]) { e.preventDefault(); pick(shown[hi]); }
        else if (e.key === 'Escape') { setQ(null); setOpen(false); }
      }} />
    <span aria-hidden="true" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: 10, opacity: 0.55, color: inputStyle?.color }}>▾</span>
    {open && shown.length > 0 && <div ref={listRef} role="listbox"
      style={{ position: 'absolute', zIndex: 40, left: 0, right: 0, top: 'calc(100% + 4px)', maxHeight: 240, overflowY: 'auto', background: bg,
        border: dark ? '1px solid rgba(255,255,255,.18)' : '1px solid rgba(0,0,0,.14)', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,.14)' }}>
      {shown.map((o, i) => <div key={o.value} role="option" aria-selected={o.value === value}
        onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
        style={{ padding: '8px 11px', fontSize: 14, cursor: 'pointer', color: inputStyle?.color,
          background: i === hi ? (dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.05)') : 'transparent',
          fontWeight: o.value === value ? 700 : 400 }}>
        {o.label}{o.hint ? <span style={{ opacity: 0.55, marginLeft: 6 }}>{o.hint}</span> : null}
      </div>)}
    </div>}
  </div>;
};
Combo.propTypes = {
  value: PropTypes.string, display: PropTypes.string, placeholder: PropTypes.string, options: PropTypes.array.isRequired,
  onPick: PropTypes.func.isRequired, onType: PropTypes.func, onFocus: PropTypes.func, inputStyle: PropTypes.object,
  dark: PropTypes.bool, ariaLabel: PropTypes.string, inputMode: PropTypes.string, free: PropTypes.bool,
};

const STATE_OPTIONS = US_STATES.map(([code, name]) => ({ value: code, label: name, hint: code }));

const UsAddressFields = ({ country, city, state, zip, onChange, inputStyle, dark = false }) => {
  const isUS = !country || String(country).toUpperCase() === 'US';
  const [places, setPlaces] = useState(null);
  const [zipHint, setZipHint] = useState('');
  const warm = () => { if (isUS && !places) loadUsPlaces().then(setPlaces).catch(() => { /* suggestions are optional */ }); };

  const stateCode = toStateCode(state);
  // Legacy free-text values ("Texas", "tx") become the code the first time we see them.
  useEffect(() => {
    if (isUS && state && stateCode && stateCode !== state) onChange({ state: stateCode });
  }, [isUS, state, stateCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const cityOptions = useMemo(() => (places && stateCode ? places.cities(stateCode) : [])
    .map((c) => ({ value: c, label: c })), [places, stateCode]);

  const onZip = (raw) => {
    const v = raw.replace(/[^\d-]/g, '').slice(0, 10);
    onChange({ zip: v });
    setZipHint('');
    if (/^\d{5}$/.test(v.slice(0, 5)) && v.length >= 5) {
      loadUsPlaces().then((p) => {
        setPlaces(p);
        const hit = p.zip(v);
        if (!hit) { setZipHint('ZIP not found — please double-check it.'); return; }
        onChange({ city: hit.city, state: hit.state });
        setZipHint(`${hit.city}, ${stateName(hit.state)}`);
      }).catch(() => {});
    }
  };

  if (!isUS) {
    return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <input style={{ ...inputStyle, flex: '2 1 160px' }} placeholder="City" value={city} onChange={(e) => onChange({ city: e.target.value })} />
      <input style={{ ...inputStyle, flex: '1 1 110px' }} placeholder="State / Region" value={state} onChange={(e) => onChange({ state: e.target.value })} />
      <input style={{ ...inputStyle, flex: '1 1 100px' }} placeholder="Postal code" value={zip} onChange={(e) => onChange({ zip: e.target.value })} />
    </div>;
  }

  return <div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: '2 1 180px', display: 'flex' }}>
        <Combo ariaLabel="City" placeholder={stateCode ? 'City' : 'City (pick a state to search)'} options={cityOptions} value={city}
          display={city ?? ''} inputStyle={inputStyle} dark={dark} free onFocus={warm}
          onType={(v) => onChange({ city: v })} onPick={(o) => onChange({ city: o.value })} />
      </div>
      <div style={{ flex: '1 1 150px', display: 'flex' }}>
        <Combo ariaLabel="State" placeholder="State" options={STATE_OPTIONS} value={stateCode}
          display={stateCode ? stateName(stateCode) : (state ?? '')} inputStyle={inputStyle} dark={dark}
          onPick={(o) => onChange(o.value === stateCode ? {} : { state: o.value, city: '' })} />
      </div>
      <div style={{ flex: '1 1 100px', display: 'flex' }}>
        <input style={inputStyle} placeholder="ZIP" aria-label="ZIP code" inputMode="numeric" autoComplete="postal-code"
          value={zip} onFocus={warm} onChange={(e) => onZip(e.target.value)} />
      </div>
    </div>
    {zipHint && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7, color: inputStyle?.color }}>{zipHint}</div>}
  </div>;
};
UsAddressFields.propTypes = {
  country: PropTypes.string, city: PropTypes.string, state: PropTypes.string, zip: PropTypes.string,
  onChange: PropTypes.func.isRequired, inputStyle: PropTypes.object, dark: PropTypes.bool,
};

export default UsAddressFields;
